import { Action, AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { add, differenceInMinutes } from "date-fns";
import { FormikProps } from "formik";
import i18next from "i18next";
import isNil from "lodash.isnil";
import find from "lodash/find";
import { toast } from "react-toastify";
import { Attachment } from "types/common.types";

import { Dispatch } from "react";

import { UploadedFile } from "components/Common/FileUploader";

import {
  deleteMeetingAttachmentSubsection,
  getMeetingAttachmentSubsection,
  postAgendaSubsection,
  postMeetingAttachmentSubsection,
} from "store/meetings/actions";
import { MeetingFormVisibilityType } from "store/meetings/reducer";
import {
  AgendaStatus,
  AgendaSubsectionRequest,
  MeetingStatus,
  SingleMeeting,
} from "store/meetings/types";
import { Section } from "store/meetings/types";
import { Subsection } from "store/meetings/types";

import { donwloadByUrl, remapAttachmentObjectToList } from "utils/files";

import { currentLanguage } from "consts";

import { CreateMeetingValues, MeetingSection } from "pages/Meetings/Form";

import {
  MeetingNoteLockedEvent,
  MeetingNoteUnlockedEvent,
} from "../types/socket.types";
import { AgendaFormData } from "./../store/meetings/types";
import { addSecondToDate } from "./dates";

type PostAgendaSection = {
  section: MeetingSection | Section;
  subsection: AgendaSubsectionRequest;
  sectionDuration: number;
  agendaId: number;
  files?: UploadedFile[];
  formik: FormikProps<CreateMeetingValues>;
  dispatch: ThunkDispatch<any, undefined, AnyAction> & Dispatch<Action<any>>;
  callbackAfterFormikUpdate: () => void;
};

type CalcDurationOnAddSubsection = {
  parentSectionIndex?: number;
  section: MeetingSection | Section;
  values: CreateMeetingValues;
};

type PostSubsectionFiles = {
  files: UploadedFile[];
  dispatch: ThunkDispatch<any, undefined, AnyAction> & Dispatch<Action<any>>;
  formik: FormikProps<CreateMeetingValues>;
  subsectionId: number;
  fieldName: string;
};

type OnDeleteSubsectionServerFile<T> = {
  attachment: Attachment;
  dispatch: ThunkDispatch<any, undefined, AnyAction> & Dispatch<Action<any>>;
  createMeetingFormik: FormikProps<CreateMeetingValues>;
  subsectionId: number;
  localFormik: FormikProps<T>;
  fieldName: string;
};

type OnDonwloadSubsectionServerFile = {
  attachment: Attachment;
  dispatch: ThunkDispatch<any, undefined, AnyAction> & Dispatch<Action<any>>;
  subsectionId: number;
  meetingId: number;
};

export type CalculateSectionTimeParam = {
  sections: Subsection[] | Section[];
  index: number;
  meetingStartTime: string;
  pastMinutes?: number;
};

export type CalculateSubsectionTimeParam = {
  meetingStartTime: string;
  sections: Section[];
  sectionIndex: number;
  subsectionIndex: number;
  subsections: Subsection[];
  pastMinutes?: number;
};

export const postSubsectionFiles = async ({
  files,
  dispatch,
  formik,
  subsectionId,
  fieldName,
}: PostSubsectionFiles) => {
  try {
    const attachmentPromises = files.map(async (file: UploadedFile) => {
      const attachmentRes = await dispatch(
        postMeetingAttachmentSubsection({
          meetingId: formik.values.id!,
          subsectionId,
          file: {
            attachment: file.file,
            description: file.description ?? "",
          },
        })
      );
      if (attachmentRes.payload.data.id) {
        return remapAttachmentObjectToList(
          attachmentRes.payload.data.attachments
        );
      }
    });

    //remove duplicates on front, from formik
    const attachments = (await Promise.all(attachmentPromises))
      .flat()
      .filter((attachment, index, self) => {
        const foundIndex = self.findIndex((a) => a?.uuid === attachment?.uuid);
        return foundIndex === index;
      });

    formik.setFieldValue(fieldName, attachments);
  } catch (error) {
    toast.error(
      `${i18next.getResource(
        currentLanguage,
        "translation",
        "errors.filesUploadError"
      )}`
    );
    throw error;
  }
};

export const postAgendaSubsectionHandler = async ({
  subsection,
  section,
  agendaId,
  sectionDuration,
  formik,
  files,
  dispatch,
  callbackAfterFormikUpdate,
}: PostAgendaSection) => {
  try {
    const postAgendaSubsectionRes = await dispatch(
      postAgendaSubsection({
        agendaId: String(agendaId),
        postAgendaSubsection: subsection,
      })
    );

    if (postAgendaSubsectionRes.payload.data.id) {
      const searchedSection = find(formik.values.agenda.sections, {
        id: section.id,
      });

      if (!isNil(searchedSection)) {
        const indexSection =
          formik.values.agenda.sections.indexOf(searchedSection);
        const sectionAfterPostSubsection = {
          ...searchedSection,
          duration: sectionDuration,
          subsections: [
            ...searchedSection.subsections,
            postAgendaSubsectionRes.payload.data,
          ],
        };
        formik.setFieldValue(
          `agenda.sections[${indexSection}]`,
          sectionAfterPostSubsection
        );
        await postSubsectionFiles({
          files: files || [],
          dispatch,
          formik,
          subsectionId: postAgendaSubsectionRes.payload.data.id,
          fieldName: `agenda.sections[${indexSection}].subsections[${searchedSection.subsections.length}].attachments`,
        });
      }
    }
  } catch (error) {
    toast.error(
      `${i18next.getResource(
        currentLanguage,
        "translation",
        "errors.subsectionPostError"
      )}`
    );
    throw error;
  }
};

export const calcDurationOnAddSubsection = ({
  section,
  parentSectionIndex,
  values,
}: CalcDurationOnAddSubsection) => {
  let sectionDuration = section.duration;

  if (
    !isNil(parentSectionIndex) &&
    values.agenda.sections[parentSectionIndex].duration >= 0 &&
    values.agenda.sections[parentSectionIndex].subsections.length === 0
  ) {
    sectionDuration = 0;
  }
  return sectionDuration;
};

export const onDeleteSubsectionServerFile = <
  T extends { serverFiles: Attachment[] }
>({
  attachment,
  dispatch,
  createMeetingFormik,
  subsectionId,
  localFormik,
  fieldName,
}: OnDeleteSubsectionServerFile<T>) => {
  dispatch(
    deleteMeetingAttachmentSubsection({
      attachmentId: attachment.uuid,
      meetingId: createMeetingFormik.values.id!,
      subsectionId,
    })
  ).then((res: any) => {
    if (res.payload.data.id) {
      const newServeFiles = localFormik.values.serverFiles.filter(
        (serverFile: Attachment) => {
          return serverFile.uuid !== attachment.uuid;
        }
      );
      localFormik.setFieldValue("serverFiles", newServeFiles);
      createMeetingFormik.setFieldValue(fieldName, newServeFiles);
    }
  });
};

export const onDonwloadSubsectionServerFile = ({
  attachment,
  dispatch,
  meetingId,
  subsectionId,
}: OnDonwloadSubsectionServerFile) => {
  dispatch(
    getMeetingAttachmentSubsection({
      attachmentId: attachment.uuid,
      meetingId,
      subsectionId,
    })
  ).then((res) => {
    if (res.payload.data.url) {
      donwloadByUrl(res.payload.data.url);
    }
  });
};

export const userIsSectionParticipant = (
  section: Section,
  loggedUserId: number,
  meetingCreatorId: number
) => {
  if (loggedUserId === meetingCreatorId) return true;
  if (section.subsections.length > 0) {
    return true;
  } else {
    return section.users.some((user) => user.id === loggedUserId);
  }
};

// =============================== AGENDA MEETING MINUTES START ===============================

type GetLoggedUserAgendaMeetingMinutesNotesProps = {
  sections: Section[];
  loggedUserId: number | undefined;
};

export const getLoggedUserAgendaMeetingMinutesNotes = ({
  sections,
  loggedUserId,
}: GetLoggedUserAgendaMeetingMinutesNotesProps) => {
  if (!loggedUserId) return undefined;
  const currentSection = sections.find(
    (section) =>
      section.status === AgendaStatus.NotStarted ||
      section.status === AgendaStatus.Current
  );
  const currentSubsection = currentSection?.subsections.find(
    (subsection) =>
      subsection.status === AgendaStatus.NotStarted ||
      subsection.status === AgendaStatus.Current
  );
  const notes = currentSubsection
    ? currentSubsection.entry?.notes
    : currentSection?.entry?.notes;
  const userNotes = notes?.find(({ creator: { id } }) => id);
  if (
    userNotes &&
    typeof userNotes.contents === "string" &&
    userNotes.contents !== ""
  ) {
    return userNotes;
  }
  return undefined;
};
// =============================== AGENDA MEETING MINUTES END ===============================
// // =============================== AGENDA MEETING MINUTES START ===============================

// type GetLoggedUserAgendaMeetingMinutesNotesProps = {
//   sections: Section[];
//   loggedUserId: number | undefined;
// };

// export const getLoggedUserAgendaMeetingMinutesNotes = ({
//   sections,
//   loggedUserId,
// }: GetLoggedUserAgendaMeetingMinutesNotesProps) => {
//   if (!loggedUserId) return undefined;
//   const currentSection = sections.find(
//     (section) =>
//       section.status === AgendaStatus.NotStarted ||
//       section.status === AgendaStatus.Current
//   );
//   const currentSubsection = currentSection?.subsections.find(
//     (subsection) =>
//       subsection.status === AgendaStatus.NotStarted ||
//       subsection.status === AgendaStatus.Current
//   );
//   const notes = currentSubsection
//     ? currentSubsection.entry?.notes
//     : currentSection?.entry?.notes;
//   const userNotes = notes?.find(({ creator: { id } }) => id === loggedUserId);
//   if (
//     userNotes &&
//     typeof userNotes.contents === "string" &&
//     userNotes.contents !== ""
//   ) {
//     return userNotes;
//   }
//   return undefined;
// };
// // =============================== AGENDA MEETING MINUTES END ===============================

export const showJoinMeetingButton = (meeting: SingleMeeting) => {
  const meetingApprovedStatus = meeting.status === MeetingStatus.Approved;
  const meetingStartedStatus = meeting.status === MeetingStatus.Started;
  const now = new Date();
  const meetingStartDate = new Date(meeting.start_date);
  const diff = differenceInMinutes(meetingStartDate, now);
  const startMeetingBeforeMinutes =
    Number(process.env.REACT_APP_CAN_START_MEETING_BEFORE_IN_HOURS || 0) * 60;

  if (diff <= -1440 && meeting.status !== MeetingStatus.Started) return false;
  return (
    diff <= startMeetingBeforeMinutes &&
    (meetingApprovedStatus || meetingStartedStatus)
  );
};

export const replaceBreakLines = (text: string) => {
  return text?.replace(/(?:\r\n|\r|\n)/g, "<br>");
};

export const remapSubsectionContentType = (
  type: "task" | "voting" | "text" | "milestone" | "result"
): MeetingFormVisibilityType => {
  switch (type) {
    case "task": {
      return "taskSubsection";
    }
    case "voting": {
      return "votingSubsection";
    }
    case "milestone": {
      return "milestoneSubsection";
    }
    default: {
      return "textSubsection";
    }
  }
};

export const calculateSectionTime = (obj: CalculateSectionTimeParam) => {
  const { sections, meetingStartTime, index, pastMinutes } = obj;
  const prevDuration = calcDurationAllPrevSections(sections, index);
  const endDate = addSecondToDate(
    new Date(meetingStartTime),
    prevDuration + sections?.[index]?.duration
  ).toISOString();

  const startDate = addSecondToDate(
    new Date(meetingStartTime),
    prevDuration
  ).toISOString();

  if (pastMinutes) {
    return {
      startDate: add(new Date(startDate), {
        minutes: pastMinutes,
      }).toISOString(),
      endDate: add(new Date(endDate), { minutes: pastMinutes }).toISOString(),
    };
  }
  return {
    endDate,
    startDate,
  };
};

export const calculateSubsectionTime = (obj: CalculateSubsectionTimeParam) => {
  const {
    sections,
    meetingStartTime,
    sectionIndex,
    subsections,
    subsectionIndex,
    pastMinutes,
  } = obj;
  const startTime = addSecondToDate(
    new Date(meetingStartTime),
    calcDurationAllPrevSections(sections, sectionIndex) +
      calcDurationAllPrevSections(subsections, subsectionIndex)
  ).toISOString();

  const endTime = addSecondToDate(
    new Date(startTime),
    subsections?.[subsectionIndex]?.duration
  ).toISOString();
  if (pastMinutes) {
    return {
      startTime: add(new Date(startTime), {
        minutes: pastMinutes,
      }).toISOString(),
      endTime: add(new Date(endTime), { minutes: pastMinutes }).toISOString(),
    };
  }
  return {
    startTime,
    endTime,
  };
};

export const sumOfSectionAndSubsection = (agendaValues: AgendaFormData) => {
  let result = 0;
  const { sections } = agendaValues;
  result += sections.length;
  sections.forEach((section) => {
    result += section.subsections.length;
  });
  return result;
};

export const calcDurationAllPrevSections = (
  list: Subsection[] | Section[],
  index: number
) => {
  let duration = 0;
  if (list) {
    for (let i = 0; i < index; i++) {
      duration += list[i]?.duration;
    }
  }
  return duration;
};

export const updateSectionNotes = (
  section: Section,
  data: MeetingNoteLockedEvent | MeetingNoteUnlockedEvent,
  lock: boolean
) => {
  const newNotes = (section.entry?.notes || []).map((sectionNote) => {
    if (data.section_class === "AgendaSection") {
      return data.note_id === sectionNote.id
        ? { ...sectionNote, locked: lock, editor_id: data.editor_id || null }
        : sectionNote;
    }
    return sectionNote;
  });
  return {
    ...section,
    entry: section.entry ? { ...section.entry, notes: newNotes } : undefined,
  };
};

export const updateSubsectionNotes = (
  subsection: Subsection,
  data: MeetingNoteLockedEvent | MeetingNoteUnlockedEvent,
  lock: boolean
) => {
  const newNotes = (subsection.entry?.notes || []).map((subSectionNote) => {
    if (data.section_class === "AgendaSubsection") {
      return data.note_id === subSectionNote.id
        ? { ...subSectionNote, locked: lock, editor_id: data.editor_id || null }
        : subSectionNote;
    }
    return subSectionNote;
  });
  return {
    ...subsection,
    entry: subsection.entry
      ? { ...subsection.entry, notes: newNotes }
      : undefined,
  };
};

export const updateSection = (
  section: Section,
  data: MeetingNoteLockedEvent | MeetingNoteUnlockedEvent,
  lock: boolean
) => {
  if (section.id !== data.section_id) {
    return section;
  }
  return updateSectionNotes(section, data, lock);
};

export const updateSubsection = (
  section: Section,
  data: MeetingNoteLockedEvent | MeetingNoteUnlockedEvent,
  lock: boolean
) => {
  const newSubsections = section.subsections.map((subsection) => {
    if (subsection.id === data.section_id) {
      return updateSubsectionNotes(subsection, data, lock);
    }
    return subsection;
  });
  return {
    ...section,
    subsections: newSubsections,
  };
};

export const getDownloadMeetingSignedSummaryUrl = (
  meetingId: number,
  summaryId: string
) => `/meetings/${meetingId}/summary/download-signed-summary/${summaryId}`;
